import React, { useState, useEffect } from 'react';
import { schoolAdminAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const PayFeePage = () => {
  const [loading, setLoading] = useState(false);
  const [school, setSchool] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [payments, setPayments] = useState([]);
  const [manualRequests, setManualRequests] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFullData();
  }, []);

  const fetchFullData = async () => {
    setLoading(true);
    try {
      // Assuming the same endpoint now returns the full object
      const res = await schoolAdminAPI.getSchoolInfo();
      const data = res.data;
      setSchool(data.school || null);
      setWallet(data.wallet || null);
      setPayments(data.payments || []);
      setManualRequests(data.manual_payment_requests || []);
    } catch (error) {
      toast.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFee = async () => {
    setLoading(true);
    try {
      const res = await schoolAdminAPI.initiatePayment({
        payment_type: 'application_fee',
        callback_url: window.location.origin + '/admin/payment-callback',
      });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error('Payment initiation failed');
      setLoading(false);
    }
  };

  if (loading && !school) return <div className="text-center py-10">Loading payment details...</div>;

  if (!school) return <div className="text-center py-10">No school data found.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      {/* School Info */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h1 className="text-2xl font-bold mb-4">School: {school.name}</h1>
        <p className="text-gray-600">Approval Status: <span className="font-medium">{school.approval_status}</span></p>
        <p className="text-gray-600">Application Fee Paid: 
          <span className={`ml-2 font-medium ${school.application_fee_paid ? 'text-green-600' : 'text-red-600'}`}>
            {school.application_fee_paid ? 'Yes' : 'No'}
          </span>
        </p>
      </div>

      {/* Wallet Balance */}
      {wallet && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-3">Wallet</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600">Credit Balance</p>
              <p className="text-2xl font-bold">{wallet.credit_balance.toLocaleString()}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600">Total Credits Purchased</p>
              <p className="text-2xl font-bold">{wallet.total_credits_purchased.toLocaleString()}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-yellow-600">Total Credits Used</p>
              <p className="text-2xl font-bold">{wallet.total_credits_used.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Action (if fee not paid) */}
      {!school.application_fee_paid && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">Pay Application Fee</h2>
          <p className="mb-4">Your school is approved. Please pay the one-time application fee to activate your account.</p>
          <button
            onClick={handlePayFee}
            disabled={loading}
            className="w-full sm:w-auto bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Pay Now with Paystack'}
          </button>
        </div>
      )}

      {/* Payment History */}
      {payments.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow overflow-x-auto">
          <h2 className="text-xl font-bold mb-4">Payment History</h2>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount (₦)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credits</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid At</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td className="px-4 py-3 text-sm capitalize">{payment.payment_type.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-sm">₦{payment.amount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm">{payment.credits ?? '-'}</td>
                  <td className="px-4 py-3 text-sm font-mono">{payment.reference}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium
                      ${payment.payment_status === 'success' ? 'bg-green-100 text-green-800' : 
                        payment.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'}`}>
                      {payment.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm capitalize">{payment.payment_method}</td>
                  <td className="px-4 py-3 text-sm">{payment.paid_at ? new Date(payment.paid_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual Payment Requests */}
      {manualRequests.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow overflow-x-auto">
          <h2 className="text-xl font-bold mb-4">Manual Payment Requests</h2>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount (₦)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested At</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved At</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {manualRequests.map((req) => (
                <tr key={req.id}>
                  <td className="px-4 py-3 text-sm">₦{req.amount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm font-mono">{req.reference}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium
                      ${req.status === 'approved' ? 'bg-green-100 text-green-800' : 
                        req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'}`}>
                      {req.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{new Date(req.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm">{req.approved_at ? new Date(req.approved_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PayFeePage;